// Section 13 and 16: every trust change the crime phase makes goes through the trustChange target
// (so norms, traits and the justice table apply) and is tallied into one `trust` report entry per
// cause at the end of the phase.
import { clamp } from '../../core/math';
import type {
  JusticeResponse,
  JusticeRole,
  PersonId,
  PhaseContext,
  Target,
  TrustCause,
} from '../../core/types';
import { num } from '../shared/value';

export interface CrimeRun {
  trust: Map<TrustCause, { delta: number; people: number }>;
  /**
   * Offenders judged in this phase (custody, exile, restitution may have changed): canOffend reads
   * them from the draft; everyone else from the committed state (Section 2 budget).
   */
  judged: Set<PersonId>;
}

export function newRun(): CrimeRun {
  return { trust: new Map(), judged: new Set() };
}

/** Apply one person's trust change; returns the change actually made. */
export function changeTrust(
  ctx: PhaseContext,
  run: CrimeRun,
  personId: PersonId,
  cause: TrustCause,
  justice?: { response: JusticeResponse; role: JusticeRole },
): number {
  const p = ctx.draft.people[personId];
  // Modifiers read the phase-start state (ADR-0010 item 20); someone not in it has no evaluation.
  if (p === undefined || ctx.state.people[personId] === undefined) return 0;
  const target: Target =
    justice === undefined
      ? { kind: 'trustChange', personId, cause }
      : { kind: 'trustChange', personId, cause, justice };
  const delta = num(ctx, target);
  if (delta === 0) return 0;
  const before = p.trust;
  p.trust = clamp(before + delta, 0, ctx.cfg.people.statMax);
  const row = run.trust.get(cause) ?? { delta: 0, people: 0 };
  row.delta += delta;
  row.people += 1;
  run.trust.set(cause, row);
  return p.trust - before;
}

/** One `trust` entry per cause, mean change per person (the bodies phase's format). */
export function reportTrust(ctx: PhaseContext, run: CrimeRun): void {
  for (const [cause, row] of [...run.trust.entries()].sort(([a], [b]) => (a < b ? -1 : 1))) {
    ctx.report.add({ kind: 'trust', cause, delta: row.delta / row.people, people: row.people });
  }
}
